<script lang="ts" setup>
import { fmtMSS } from '~/const/conversion'
import { getStaffSearch } from '~/queries/work'

const { locale } = useI18n()
const route = useRoute()
const router = useRouter()

const getElectronic = (electronic) => {
  switch (electronic) {
    case 'true':
      return 'Oui'
    case 'false':
      return 'Non'
    default:
      return 'Inconnu'
  }
}
const { data, pending, error } = await useAsyncData('data', () =>
  execQuery(getStaffSearch, {
    ...route.query,
    ...(route.query.electronic || route.query.electronic === 'unknown'
      ? route.query.electronic === 'true'
        ? { electronic: true }
        : { electronic: false }
      : {}),
  })
)

function formatWork(work) {
  if (!work) return null
  let start = work.publishingStartDate
  let end = work.publishingEndDate
  let title = work.title
  let excerpt
  if (locale.value === 'fr') {
    excerpt = work.description ?? ''
  } else {
    excerpt = work.descriptionEn ?? ''
  }
  if (work.duration) {
    excerpt += `${excerpt ? ', ' : ''}${fmtMSS(work.duration)}`
  }
  if (start) {
    start = new Date(start).getFullYear()
    title += ` (${start}`
    if (end) {
      end = new Date(end).getFullYear()
      if (end !== start) {
        title += `-${end}`
      }
    }
    title += ')'
  }
  const subtitle = work.composers
    ?.map(({ firstName, lastName }) => `${firstName} ${lastName}`)
    .join(', ')
  return {
    ...work,
    type: 'work',
    title,
    excerpt,
    subtitle,
  }
}

const resultList = computed(() => {
  if (pending.value || error.value) return []
  return data.value.data?.staffSearch.map(formatWork)
})

let workValues = computed(() => {
  if (pending.value || error.value) {
    return {
      genre: null,
      electronic: [],
      withInstruments: [],
      withoutInstruments: [],
    }
  }
  return {
    genre: data.value.data?.staffSearchData?.genres.find(
      ({ slug }) => slug === route.query.genre
    ),
    electronic: {
      slug: route.query.electronic,
      name: getElectronic(route.query.electronic),
    },
    withInstruments: data.value.data?.staffSearchData?.thesauri.filter(
      ({ slug }) =>
        route.query.withInstruments
          ?.split(',')
          .find((instrument) => instrument === slug)
    ),
    withoutInstruments: data.value.data?.staffSearchData?.thesauri.filter(
      ({ slug }) =>
        route.query.withoutInstruments
          ?.split(',')
          .find((instrument) => instrument === slug)
    ),
  }
})

const workFields = computed(() => {
  if (pending.value || error.value) return []

  console.log(
    data.value.data?.staffSearchData?.genres?.find(
      ({ slug }) => slug === '2-cordes-et-ensembleorchestre'
    )
  )
  return [
    {
      id: 1,
      type: 'autocomplete',
      label: 'Genre',
      name: 'genre',
      placeholder: '',
      options: data.value.data?.staffSearchData?.genres,
      model: 'genre',
      multiple: false,
      displayFn: (genre) =>
        locale.value === 'fr' ? genre?.nameFr : genre?.nameEn,
    },
    {
      id: 2,
      type: 'autocomplete',
      label: 'Avec électronique',
      name: 'electonic',
      placeholder: '',
      options: [
        { name: 'Inconnu', nameEn: 'Unknown', slug: 'unknown' },
        { name: 'Oui', nameEn: 'Yes', slug: 'true' },
        { name: 'Non', nameEn: 'No', slug: 'false' },
      ],
      model: 'electronic',
      multiple: false,
      displayFn: (electronic) =>
        locale.value === 'fr' ? electronic?.name : electronic?.nameEn,
    },
    {
      id: 3,
      type: 'autocomplete',
      label: 'Avec les instruments',
      name: 'withInstruments',
      placeholder: '',
      options: data.value.data?.staffSearchData?.thesauri,
      model: 'withInstruments',
      multiple: true,
      displayFn: (instrument) =>
        locale.value === 'fr' ? instrument?.nameFr : instrument?.nameEn,
    },
    {
      id: 4,
      type: 'autocomplete',
      label: 'Sans les instruments',
      name: 'withoutInstruments',
      placeholder: '',
      options: data.value.data?.staffSearchData?.thesauri,
      model: 'withoutInstruments',
      multiple: true,
      displayFn: (instrument) =>
        locale.value === 'fr' ? instrument?.nameFr : instrument?.nameEn,
    },
  ]
})

const currentPage = ref(1)
function stripObj(obj) {
  if (typeof obj == 'string') {
    return obj
  }
  return obj.slug
}
async function staffSearch(workValues) {
  const query = {}
  if (workValues.electronic) {
    query.electronic = stripObj(workValues.electronic)
  }
  if (workValues.genre) {
    query.genre = stripObj(workValues.genre)
  }
  if (workValues.withoutInstruments?.length > 0) {
    query.withoutInstruments = workValues.withoutInstruments
      .map(stripObj)
      .join(',')
  }
  if (workValues.withInstruments?.length > 0) {
    query.withInstruments = workValues.withInstruments.map(stripObj).join(',')
  }
  await router.push({
    query,
  })
}
</script>
<template>
  <div>
    <PatternHero size="small" color-class="text-primary-400">
      <div class="p-3 lg:p-6">
        <h2
          class="font-semibold first-letter:uppercase text-mobile-lg md:text-lg"
        >
          {{ $t('labels.staff_search') }}
        </h2>
      </div>
    </PatternHero>
    <div class="mx-auto max-w-7xl px-6 xl:px-0">
      <form class="px-24">
        <div v-for="field in workFields" :key="field.id">
          <div v-if="field.type === 'autocomplete'" class="my-2">
            <FormAutocomplete
              :data="workValues"
              :options="field.options"
              :label="field.label"
              :model="field.model"
              :multiple="field.multiple"
              :display-fn="field.displayFn"
            />
          </div>
        </div>
        <BasicButton @click.prevent="staffSearch(workValues, currentPage)">
          <span class="first-letter:uppercase">
            {{ $t('labels.submit') }}
          </span>
        </BasicButton>
      </form>
      <div class="mx-auto flex max-w-7xl gap-7 px-24 mt-4 lg:gap-14">
        <div ref="resultList" class="pt-6 md:pt-0">
          <div class="divide-y divide-shade-200">
            <ListResource
              v-for="(result, i) in resultList"
              :key="i"
              :resource="result"
              class="py-4 first:mt-4 md:p-4 md:first:mt-0"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
